<script setup lang="ts">
import {
  ContractManagementContractFileType,
  DocumentState,
  VaultFileRevisionKind,
  type ContractManagementContractFileInDb,
  type ContractManagementContractFileInDbExtended,
  type ContractManagementExtractionInDb,
  type ContractManagementContractFileUpdateUser,
  type ContractManagementContractUpdate,
  type ContractManagementViewColumnInDb,
  type ContractManagementContractInDb,
  type ContractRoleShareInDb,
  type ContractTypeExtractionEntry,
  type ContractUserShareInDb,
  type VaultFileRevisionInDb,
  type ContractManagementObligationInDb,
  type ContractManagementClauseInDb,
  type ContractManagementDateInDb,
  TaskStatus,
  type SigningRequestStatusEntry,
  type SigningRequestWithRelationsInDb,
  addContractualPartyToContractFile,
  deleteContractManagementDate,
  getContractManagementContractFileContractualParties,
  removeContractualPartyFromContractFile,
} from '@/api';
import type { ContractualParty } from '@/services/contractManagement/contractualPartyRow';
import { toDateRow } from '@/services/contractManagement/dateRow';
import { staticEntityName } from '@/utils/contractManagementView/staticEntityName';
import EntityFormDialog from '@/views/contractManagement/EntityFormDialog.vue';
import { fileDateConfig } from '@/views/contractManagement/entityListConfig';
import type { EntityType } from '@/types/contractManagement/entityType';
import { enumToSelectOptionsWithLabels } from '@/utils/enumUtils';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import { ContractFileDownloadService } from '@/services/contractManagement/contractFileDownloadService';
import { ContractFileDetailService } from '@/services/contractManagement/contractFileDetailService';
import { SignatureRequestService } from '@/services/contractManagement/signatureRequestService';
import { ContractShareService } from '@/services/contractShareService';
import { IconEye, IconEyeOff, IconInfo, IconMore } from '@/components/design/icons';
import { contractFileTypeLabel } from '@/components/design/statusMaps';
import GaiaContractStateCell from '@/components/design/domain/contractManagement/GaiaContractStateCell.vue';
import { fileTypeHasState } from '@/components/design/domain/contractManagement/contractStateIcons';
import { isConversionRunning, isPdfFile } from '@/utils/fileTypeUtils';
import {
  documentStateFromMenuKey,
  documentStateMenuOption,
} from '@/components/file_vault/documentStateMenu';
import {
  ACCESS_PERMISSION_ADMIN,
  ACCESS_PERMISSION_WRITE,
  deniedReason,
  formatAccessPermissions,
} from '@/utils/accessPermission';
import { initialsOf } from '@/utils/initials';
import { isEffectivelyEmpty } from '@/utils/displayAiResponse';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaEditButton from '@/components/design/base/GaiaEditButton.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaMenuSurface from '@/components/design/base/GaiaMenuSurface.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaSplit from '@/components/design/base/GaiaSplit.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaTagInput from '@/components/design/base/inputs/GaiaTagInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaAvatarStack from '@/components/design/composite/GaiaAvatarStack.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaMetaItem from '@/components/design/composite/GaiaMetaItem.vue';
import GaiaMetaStrip from '@/components/design/composite/GaiaMetaStrip.vue';
import GaiaNotFound from '@/components/design/composite/GaiaNotFound.vue';
import GaiaPageHeader from '@/components/design/composite/GaiaPageHeader.vue';
import GaiaPickerOption from '@/components/design/composite/GaiaPickerOption.vue';
import GaiaTabCard from '@/components/design/composite/GaiaTabCard.vue';
import type { GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import GaiaContractFileEntityList, {
  type GaiaEntityListRow,
} from '@/components/design/domain/contractManagement/GaiaContractFileEntityList.vue';
import GaiaContractFileDateChips from '@/components/design/domain/contractManagement/GaiaContractFileDateChips.vue';
import GaiaContractFilePartyChips from '@/components/design/domain/contractManagement/GaiaContractFilePartyChips.vue';
import GaiaContractFileVersionsTable from '@/components/design/domain/contractManagement/GaiaContractFileVersionsTable.vue';
import GaiaContractTypeFieldsTable, {
  type GaiaFieldsVersion,
} from '@/components/design/domain/contractManagement/GaiaContractTypeFieldsTable.vue';
import GaiaSigningHistoryPanel from '@/components/design/domain/contractManagement/GaiaSigningHistoryPanel.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import ContractManagementExtractionDetailsModal from '@/components/contractManagement/ContractManagementExtractionDetailsModal.vue';
import MassShareContractsModal from '@/components/contractManagement/MassShareContractsModal.vue';
import MoveContractFileModal from '@/components/contractManagement/MoveContractFileModal.vue';
import SelectEntityModal from '@/components/contractManagement/SelectEntityModal.vue';
import VaultFileRevisionUploadModal from '@/components/file_vault/VaultFileRevisionUploadModal.vue';
import GaiaVaultCrumbs from '@/components/design/domain/fileVault/GaiaVaultCrumbs.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { askAiLimitWarning, askAiRoute } from '@/utils/askAi';
import { createReusableTemplate, useDebounceFn } from '@vueuse/core';
import { useVaultStore } from '@/stores/vaultStore';
import { useAuthStore } from '@/stores/auth';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { logger } from '@/utils/logger';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import { extractErrorDetail, isAccessDenied } from '@/utils/errorHandler';
import { safeStringify } from '@/utils/jsonHelpers';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { useContractManagementExtractionWebSocket } from '@/composables/useContractManagementExtractionWebSocket';
import { contractRoute } from '@/router/links';
import { contractFolderRoute } from '@/services/vaultLocationService';
import { ContractsService } from '@/services/contractManagement/contractsService';

// ==================== Router & State ====================
const route = useRoute();
const router = useRouter();
const message = useGaiaToast();

function handleAskAi(): void {
  const contractFileUids = [contractFileUid.value].filter(Boolean);
  const limitWarning = askAiLimitWarning({ contractFileUids });
  if (limitWarning) {
    message.warning(limitWarning);
    return;
  }
  void router.push(askAiRoute({ contractFileUids }));
}
const vaultStore = useVaultStore();
const authStore = useAuthStore();
const contractTypeStore = useContractTypeStore();

const contractFileUid = computed(() => route.params.uid as string);
const currentContractUid = computed(() => contractFile.value?.contract.uid ?? null);

// State
const contractFile = ref<ContractManagementContractFileInDbExtended | null>(null);
/** The backend refused the file: deleted, no access, or another organisation's. */
const unavailable = ref(false);
const loadingFile = ref<boolean>(false);

// ==================== Revision preview state ====================
const revisions = ref<VaultFileRevisionInDb[]>([]);
const selectedRevisionUid = ref<string | null>(null); // null before revisions load; current UID = no filter

const currentRevisionUid = computed(
  () => contractFile.value?.vault_file?.current_revision?.uid ?? null
);

// null when current version selected (no filter needed), actual UID otherwise
const filterRevisionUid = computed(() => {
  if (!selectedRevisionUid.value || selectedRevisionUid.value === currentRevisionUid.value) {
    return null;
  }
  return selectedRevisionUid.value;
});

const isPreviewing = computed(() => filterRevisionUid.value !== null);

const previewRevisionLabel = computed(() => {
  const r = revisions.value.find(x => x.uid === selectedRevisionUid.value);
  return r ? `v${r.version_number}` : '';
});

const selectedRevision = computed(
  () => revisions.value.find(r => r.uid === selectedRevisionUid.value) ?? null
);

async function loadRevisions(): Promise<void> {
  if (!vaultFileUid.value) return;
  try {
    const res = await vaultStore.listRevisions(vaultFileUid.value, 50, 0);
    revisions.value = res.results;
    // Set initial selection to current revision (filterRevisionUid stays null — no refetch)
    if (currentRevisionUid.value) {
      selectedRevisionUid.value = currentRevisionUid.value;
    }
  } catch {
    // non-critical — the Versions table just won't show older versions
  }
}

async function handleRevisionUploaded(): Promise<void> {
  // Re-fetch contract file to update current_revision (file name, version number, etc.)
  await fetchContractFile();
  // Reload revisions list and select the new current revision
  await loadRevisions();
  // Refresh the fields and static models for the new revision
  await Promise.all([fetchCoreStaticModels(), fetchFields()]);
  void loadVersionEntries();
}

// ==================== Static Models State ====================
const obligations = ref<ContractManagementObligationInDb[]>([]);
const clauses = ref<ContractManagementClauseInDb[]>([]);
const contractualParties = ref<ContractualParty[]>([]);
const dates = ref<ContractManagementDateInDb[]>([]);
const loadingStaticModels = ref<boolean>(false);

// Watch the effective filter UID, not raw selection, to avoid refetch when selecting current
watch(filterRevisionUid, async (): Promise<void> => {
  if (unavailable.value) return;
  await Promise.all([fetchCoreStaticModels(), fetchFields()]);
});

// Reload the PDF preview when the selected revision changes
watch(selectedRevisionUid, () => {
  selectedPage.value = null;
  selectedExtraction.value = null;
  currentHighlights.value = [];
  pdfKey.value++;
});

// ==================== Contract-type fields ====================
/** The contract type's prompts, with the read version's answers. */
const fieldEntries = ref<ContractTypeExtractionEntry[]>([]);
/** Standalone prompts (no contract type, e.g. a view's question column) the read version was asked. */
const customFieldEntries = ref<ContractTypeExtractionEntry[]>([]);
const loadingFields = ref<boolean>(false);
/** "Show per version" is open. */
const comparingVersions = ref(false);
/**
 * Each version's answers, by revision uid — the per-version columns and the
 * Versions table's counts. Null while that version's request is in flight.
 */
const versionEntries = ref<Record<string, ContractTypeExtractionEntry[] | null>>({});
/** Each version's standalone answers, by revision uid — the Custom Extractions tab's per-version columns. */
const customVersionEntries = ref<Record<string, ContractTypeExtractionEntry[] | null>>({});
/** Versions read for the comparison: the newest ones, one request each. */
const MAX_COMPARED_VERSIONS = 10;

// Extraction edit modal state
const showExtractionEditModal = ref<boolean>(false);
const editingExtraction = ref<{
  columnUid: string;
  extraction: ContractManagementExtractionInDb | null;
  column: ContractManagementViewColumnInDb | null;
  contractUid?: string;
  fileUid?: string;
} | null>(null);

// Edit/Delete state
const showEditModal = ref<boolean>(false);
const showMoveModal = ref<boolean>(false);
const showRevisionUploadModal = ref<boolean>(false);
const editFormData = ref<{
  /** Null until the AI has classified the file. */
  type: ContractManagementContractFileType | null;
  description: string;
}>({
  type: null,
  description: '',
});
const saving = ref<boolean>(false);
const deleting = ref<boolean>(false);
const downloading = ref<boolean>(false);

// PDF preview state — the document sits beside the fields unless hidden
const showPdf = ref(true);
const selectedPage = ref<number | null>(null);
const selectedExtraction = ref<ContractManagementExtractionInDb | null>(null);
const currentHighlights = ref<string[]>([]);
const pdfKey = ref(0);
const pdfPreviewRef = ref<InstanceType<typeof GaiaVaultFilePdfPreview>>();

// Split-pane drag fires @update:size on every pixel of movement. Without
// debouncing, each fire remounts GaiaVaultFilePdfPreview (via :key="pdfKey"),
// and each remount re-runs its immediate watcher + getVaultFileMetadata —
// hundreds of requests per drag. Debounce so the remount only happens once
// after the user stops dragging.
const handleSplitResize = useDebounceFn((): void => {
  pdfKey.value++;
}, 200);

// ==================== Computed Properties ====================

const contract = computed(() => contractFile.value?.contract ?? null);

const vaultFileName = computed(() => {
  return (
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    selectedRevision.value?.file_name ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    contractFile.value?.vault_file?.current_revision?.file_name ||
    'Unknown File'
  );
});

const vaultFileUid = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
  return contractFile.value?.vault_file?.uid || null;
});

const vaultFileVersionNumber = computed(
  () => contractFile.value?.vault_file?.current_revision?.version_number ?? null
);

const fileType = computed(() =>
  contractFile.value ? contractFileTypeLabel(contractFile.value.type) : ''
);

const contractFileDescription = computed(() => contractFile.value?.description ?? '');

const hasState = computed(() => fileTypeHasState(contractFile.value?.type));

/**
 * The caller's bits: a contract file carries its contract's, on its vault file
 * (and on the contract in this response). Null — not computed — gates nothing.
 */
const fileBits = computed(
  () => contractFile.value?.vault_file?.permissions ?? contractFile.value?.contract.permissions
);
const writeBlock = computed(() => deniedReason(fileBits.value, ACCESS_PERMISSION_WRITE));
/** Matter, type and tags are the contract's, so they gate on its bits. */
const contractWriteBlock = computed(() =>
  deniedReason(contract.value?.permissions, ACCESS_PERMISSION_WRITE)
);
const shareBlock = computed(() =>
  deniedReason(contract.value?.permissions, ACCESS_PERMISSION_ADMIN)
);

/** Disables a menu entry the bits rule out, with the reason as its title. */
function gateOption(option: Record<string, unknown>, flag: number): Record<string, unknown> {
  const reason = deniedReason(fileBits.value, flag);
  if (!reason) return option;
  const props = (option.props as Record<string, unknown> | undefined) ?? {};
  return { ...option, disabled: true, props: { ...props, title: reason } };
}

/**
 * Everything but signing lives behind the ⋯. Reading the document and taking a
 * copy of it lead — they are the two you reach for most — then the rest of the
 * file's own actions, with the destructive one alone past a divider.
 */
const headerMenuOptions = computed((): Record<string, unknown>[] => {
  const items: Record<string, unknown>[] = [
    { key: 'download', label: downloading.value ? 'Downloading…' : 'Download' },
    { key: 'divider-view', type: 'divider' },
    contractFile.value?.vault_file?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED
      ? {
          key: 'ask-ai',
          label: 'Ask AI',
          disabled: true,
          props: { title: 'A draft has no text for the AI to read until it is saved.' },
        }
      : { key: 'ask-ai', label: 'Ask AI' },
  ];
  if (vaultFileUid.value) {
    items.push(
      gateOption({ key: 'add-revision', label: 'Add revision' }, ACCESS_PERMISSION_WRITE),
      { key: 'versions', label: 'Manage versions' }
    );
    // Sets the state of the revision being read, as the Status cell shows it.
    // Appendices take no part in the contract's state, so there is none to set,
    // and a draft revision is always "In draft" (the backend refuses a change).
    if (
      selectedRevision.value &&
      selectedRevision.value.kind !== VaultFileRevisionKind.TEMPLATED &&
      hasState.value
    ) {
      items.push(
        gateOption(
          documentStateMenuOption(selectedRevision.value.document_state),
          ACCESS_PERMISSION_WRITE
        )
      );
    }
    // As in the vault: the file's current revision, PDFs only, held while a
    // conversion runs.
    const current = contractFile.value?.vault_file?.current_revision;
    if (current && isPdfFile(current.mime_type, current.file_name)) {
      const converting = isConversionRunning(current.conversion_status);
      items.push(
        gateOption(
          {
            key: 'convert-to-docx',
            label: converting ? 'Conversion in progress' : 'Convert to DOCX',
            disabled: converting,
          },
          ACCESS_PERMISSION_WRITE
        )
      );
    }
  }
  // Platform admins only: asks the read version every question anew.
  if (canRerunExtractions.value) {
    items.push({
      key: 'rerun-extractions',
      label: rerunningAll.value ? 'Rerunning extractions…' : 'Rerun all extractions',
      disabled: rerunningAll.value,
    });
  }
  items.push(
    gateOption({ key: 'edit', label: 'Edit details' }, ACCESS_PERMISSION_WRITE),
    gateOption({ key: 'move', label: 'Move to another contract' }, ACCESS_PERMISSION_ADMIN),
    { key: 'divider', type: 'divider' },
    gateOption(
      { key: 'delete', label: 'Delete file', props: gaiaDangerOption },
      ACCESS_PERMISSION_ADMIN
    )
  );
  return items;
});

function onHeaderMenu(key: string): void {
  const documentState = documentStateFromMenuKey(key);
  if (documentState !== undefined) {
    void setDocumentState(documentState);
    return;
  }
  switch (key) {
    case 'download':
      void handleDownload();
      return;
    case 'ask-ai':
      handleAskAi();
      return;
    case 'convert-to-docx':
      void convertToDocx();
      return;
    case 'add-revision':
      showRevisionUploadModal.value = true;
      return;
    case 'rerun-extractions':
      confirmRerunAll.value = true;
      return;
    case 'versions':
      void router.push({ name: 'vault-file-versions', params: { uid: vaultFileUid.value } });
      return;
    case 'edit':
      handleEdit();
      return;
    case 'move':
      handleMove();
      return;
    case 'delete':
      confirmDelete.value = true;
  }
}

/** Sets the lifecycle state of the selected revision. */
async function setDocumentState(state: DocumentState): Promise<void> {
  const revision = selectedRevision.value;
  if (!revision) return;
  await setRevisionState(revision.uid, state);
}

/** Sets the lifecycle state of one revision — the ⋯ menu's or the signing drawer's. */
async function setRevisionState(revisionUid: string, state: DocumentState): Promise<void> {
  if (!vaultFileUid.value) return;
  try {
    const updated = await vaultStore.updateRevision(vaultFileUid.value, revisionUid, {
      document_state: state,
    });
    revisions.value = revisions.value.map(r => (r.uid === updated.uid ? updated : r));
  } catch (error) {
    logger.error('Failed to update the document state:', error);
    // The backend refuses some transitions (422) and says why.
    message.error(extractErrorDetail(error, 'Failed to update the state'));
  }
}

/**
 * Starts the PDF → DOCX conversion, then reloads the file quietly (no header
 * shimmer) so the menu shows it as in progress.
 */
async function convertToDocx(): Promise<void> {
  if (!vaultFileUid.value) return;
  try {
    const result = await vaultStore.convertPdfToDocx(vaultFileUid.value);
    message.success(result.message || `Started converting ${vaultFileName.value} to DOCX`);
    contractFile.value = await ContractFileDetailService.getContractFile(contractFileUid.value);
  } catch (error) {
    logger.error('Failed to convert file to DOCX:', error);
    message.error(
      `Failed to convert file to DOCX: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

const confirmDelete = ref<boolean>(false);

function confirmDeleteNow(): void {
  confirmDelete.value = false;
  void handleDelete();
}

const createdAt = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  const date = selectedRevision.value?.created_at || contractFile.value?.created_at;
  return date ? formatDate(date) : '—';
});

const updatedAt = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  const date = selectedRevision.value?.updated_at || contractFile.value?.updated_at;
  return date ? formatDate(date) : '—';
});

// ==================== Data Fetching ====================

async function fetchContractFile(): Promise<void> {
  loadingFile.value = true;
  try {
    contractFile.value = await ContractFileDetailService.getContractFile(contractFileUid.value);
  } catch (error) {
    if (isAccessDenied(error)) {
      // An answer, not a failure: the page renders "not available" instead.
      logger.warn('Contract file not available:', error);
      unavailable.value = true;
      return;
    }
    logger.error('Error fetching contract file:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch contract file';
    message.error(errorMessage);
  } finally {
    loadingFile.value = false;
  }
}

/**
 * Fetch all static models for the contract file
 * This is called once on mount and after any mutations
 */
async function fetchCoreStaticModels(): Promise<void> {
  loadingStaticModels.value = true;
  const revUid = filterRevisionUid.value;
  try {
    const uid = contractFileUid.value;
    // Parties link to the file itself, so they don't follow the revision filter.
    const [obligationsData, clausesData, { data: partiesData }, datesData] = await Promise.all([
      revUid
        ? ContractFileDetailService.getObligationsByRevision(uid, revUid)
        : ContractFileDetailService.getObligations(uid),
      ContractFileDetailService.getClauses(uid, revUid),
      getContractManagementContractFileContractualParties({ path: { uid }, throwOnError: true }),
      ContractFileDetailService.getDates(uid, revUid),
    ]);
    obligations.value = obligationsData;
    clauses.value = clausesData;
    contractualParties.value = partiesData;
    dates.value = datesData;
  } catch (error) {
    logger.error('Error fetching static models:', error);
    message.error(error instanceof Error ? error.message : 'Failed to fetch static models');
  } finally {
    loadingStaticModels.value = false;
  }
}

/**
 * The contract type's prompts with the read version's answers. `quiet` keeps
 * the rows on screen (a live update), where a first load shows the skeleton.
 */
let fieldsRequest = 0;
async function fetchFields(quiet = false): Promise<void> {
  // Only the latest request lands: an earlier one (another version, the old
  // type) resolving last must not overwrite it or end its loading.
  const request = ++fieldsRequest;
  if (!quiet) loadingFields.value = true;
  try {
    const [result, standalone] = await Promise.all([
      ContractFileDetailService.getContractTypeExtractions(
        contractFileUid.value,
        filterRevisionUid.value
      ),
      ContractFileDetailService.getStandaloneExtractions(
        contractFileUid.value,
        filterRevisionUid.value
      ),
    ]);
    if (request === fieldsRequest) {
      fieldEntries.value = result.results ?? [];
      customFieldEntries.value = standalone;
    }
  } catch (error) {
    logger.error('Error fetching contract-type fields:', error);
    if (!quiet && request === fieldsRequest) {
      message.error(error instanceof Error ? error.message : 'Failed to fetch fields');
    }
  } finally {
    if (request === fieldsRequest) loadingFields.value = false;
  }
}

/**
 * Every compared version's answers, one request per version, each landing as
 * it arrives. Feeds the per-version columns and the Versions table's counts.
 */
let versionEntriesRequest = 0;
/**
 * `reset` drops the answers on screen first — after a type change they are
 * another type's. A live update keeps them until the new ones land.
 */
async function loadVersionEntries(reset = false): Promise<void> {
  // Only the latest call's answers land; an earlier call's resolving last is dropped.
  const request = ++versionEntriesRequest;
  const typed = Boolean(contract.value?.contract_type);
  const targets = revisions.value.slice(0, MAX_COMPARED_VERSIONS);
  // Standalone answers don't hang off the type: an untyped contract still has them.
  versionEntries.value = typed
    ? Object.fromEntries(
        targets.map(revision => [
          revision.uid,
          reset ? null : (versionEntries.value[revision.uid] ?? null),
        ])
      )
    : {};
  customVersionEntries.value = Object.fromEntries(
    targets.map(revision => [revision.uid, customVersionEntries.value[revision.uid] ?? null])
  );
  async function loadTyped(revisionUid: string): Promise<void> {
    try {
      const result = await ContractFileDetailService.getContractTypeExtractions(
        contractFileUid.value,
        revisionUid
      );
      if (request !== versionEntriesRequest) return;
      versionEntries.value = { ...versionEntries.value, [revisionUid]: result.results ?? [] };
    } catch (error) {
      logger.error('Error fetching version fields:', error);
    }
  }
  async function loadStandalone(revisionUid: string): Promise<void> {
    try {
      const standalone = await ContractFileDetailService.getStandaloneExtractions(
        contractFileUid.value,
        revisionUid
      );
      if (request !== versionEntriesRequest) return;
      customVersionEntries.value = { ...customVersionEntries.value, [revisionUid]: standalone };
    } catch (error) {
      logger.error('Error fetching version custom extractions:', error);
    }
  }
  await Promise.all(
    targets.flatMap(revision =>
      typed
        ? [loadTyped(revision.uid), loadStandalone(revision.uid)]
        : [loadStandalone(revision.uid)]
    )
  );
}

/** Rerunning extractions is for platform admins only. */
const canRerunExtractions = computed(() => authStore.userPermissions.is_platform_admin);
/** "Rerun all" is in flight. */
const rerunningAll = ref(false);
const confirmRerunAll = ref(false);

function confirmRerunAllNow(): void {
  confirmRerunAll.value = false;
  void handleRerunAll();
}

/**
 * Asks the read version every question anew. The rows re-read straight away
 * show the questions running; each settles in through the extraction socket.
 */
async function handleRerunAll(): Promise<void> {
  rerunningAll.value = true;
  try {
    await ContractFileDetailService.rerunExtractions(
      contractFileUid.value,
      filterRevisionUid.value
    );
    message.success('Rerunning extractions');
    await fetchFields(true);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to rerun extractions');
  } finally {
    rerunningAll.value = false;
  }
}

async function handleRerunExtraction(extraction: ContractManagementExtractionInDb): Promise<void> {
  try {
    await ContractFileDetailService.rerunExtraction(contractFileUid.value, extraction.uid);
    await fetchFields(true);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to rerun the extraction');
  }
}

/** An answered prompt: asked, settled, and not empty. */
function isAnswered(entry: ContractTypeExtractionEntry): boolean {
  const extraction = entry.extraction;
  if (!extraction) return false;
  return !isEffectivelyEmpty(extraction.human_overwrite?.value ?? extraction.value);
}

const fieldCounts = computed(() => {
  const counts: Record<string, number> = {};
  for (const [uid, entries] of Object.entries(versionEntries.value)) {
    if (entries) counts[uid] = entries.filter(isAnswered).length;
  }
  return counts;
});

/** The per-version columns, oldest on the left, as the versions were made. */
function toVersionColumns(
  entriesByRevision: Record<string, ContractTypeExtractionEntry[] | null>
): GaiaFieldsVersion[] {
  const readNumber = selectedRevision.value?.version_number ?? Number.POSITIVE_INFINITY;
  return revisions.value
    .slice(0, MAX_COMPARED_VERSIONS)
    .map(revision => ({
      uid: revision.uid,
      label: `v${revision.version_number}`,
      meta: formatDateForDisplay(revision.created_at),
      older: revision.version_number < readNumber,
      entries: entriesByRevision[revision.uid] ?? null,
    }))
    .reverse();
}

const comparedVersions = computed(() => toVersionColumns(versionEntries.value));
const comparedCustomVersions = computed(() => toVersionColumns(customVersionEntries.value));

// ==================== Date Formatting ====================

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ==================== Edit/Delete/Download Handlers ====================

// Options for file type select
const fileTypeOptions = enumToSelectOptionsWithLabels(ContractManagementContractFileType);

function handleEdit(): void {
  if (!contractFile.value) return;

  editFormData.value = {
    type: contractFile.value.type,
    description: contractFile.value.description ?? '',
  };
  showEditModal.value = true;
}

async function handleSaveEdit(): Promise<void> {
  if (!contractFile.value) return;

  saving.value = true;
  try {
    const updateData: ContractManagementContractFileUpdateUser = {
      // A PUT cannot clear the type; while unclassified it is left out.
      type: editFormData.value.type ?? undefined,
      description: editFormData.value.description.trim() || null,
    };

    await ContractFilesService.update(contractFile.value.uid, updateData);
    message.success('Contract file updated successfully');
    showEditModal.value = false;

    // Refresh contract file data
    await fetchContractFile();
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to update contract file';
    message.error(errorMsg);
  } finally {
    saving.value = false;
  }
}

function handleCancelEdit(): void {
  showEditModal.value = false;
  editFormData.value = { type: null, description: '' };
}

function handleMove(): void {
  showMoveModal.value = true;
}

/** Why this file can't go out for signing; null when it can — a draft must be finished first. */
const signBlock = computed<string | null>(() => {
  if (writeBlock.value) return writeBlock.value;
  return contractFile.value?.vault_file?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED
    ? 'Finish the draft before sending it for signing.'
    : null;
});

function handleSignThis(): void {
  if (!currentContractUid.value) {
    message.warning('The parent contract is not available for signature preparation yet.');
    return;
  }

  void router.push({
    name: 'signing-contract-request-new',
    params: { uid: currentContractUid.value },
    query: {
      contract_file_uid: contractFileUid.value,
    },
  });
}

/** A signed version has nothing left to sign: the primary takes its copy instead. */
const isSigned = computed(() => selectedRevision.value?.document_state === DocumentState.SIGNED);

async function handleMoved(
  _contractFileUid: string,
  updatedFile?: ContractManagementContractFileInDb & { contract?: ContractManagementContractInDb },
  targetContract?: ContractManagementContractInDb
): Promise<void> {
  // Resolve contract for File Information display: modal always passes targetContract (existing = selected contract, new = display contract or API response)
  const displayContract = targetContract ?? updatedFile?.contract;

  if (updatedFile && displayContract) {
    // Merge updated file with display contract so the contract's fields update
    contractFile.value = { ...updatedFile, contract: displayContract };
  } else {
    await fetchContractFile();
  }
}

async function handleDelete(): Promise<void> {
  if (!contractFile.value) return;

  // Read before the await: the record is gone once the delete resolves. The
  // contract's folder too — deleting its last file deletes the contract.
  const contractUid = contractFile.value.contract.uid;
  deleting.value = true;
  try {
    const landing = await contractFolderRoute('contract_file', contractFile.value.uid);
    await ContractFilesService.delete(contractFile.value.uid);
    const contractRemains = await ContractsService.getById(contractUid).then(
      () => true,
      () => false
    );
    message.success(contractRemains ? 'Contract file deleted' : 'File and contract deleted');
    void router.push(contractRemains ? contractRoute(contractUid) : landing);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to delete contract file';
    message.error(errorMsg);
  } finally {
    deleting.value = false;
  }
}

async function handleDownload(): Promise<void> {
  const revision = selectedRevision.value;
  if (!revision) {
    message.warning('No file version available for download.');
    return;
  }

  downloading.value = true;
  try {
    const contentType = revision.mime_type || 'application/octet-stream';
    const blob = await ContractFileDownloadService.downloadVaultFile(revision.uid, contentType);
    const fileName =
      revision.file_name ||
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      contractFile.value?.description ||
      `contract-file-${contractFileUid.value.slice(0, 8)}`;
    ContractFileDownloadService.triggerDownload(blob, fileName);
    message.success('File downloaded successfully');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to download file';
    message.error(errorMessage);
  } finally {
    downloading.value = false;
  }
}

// ==================== Contract fields: matter, type, tags ====================

/**
 * Writes one of the contract's own fields from this page. The update's answer
 * leaves out the matters, so the ones already on the page are kept.
 */
async function updateContract(
  update: ContractManagementContractUpdate,
  success: string
): Promise<boolean> {
  const current = contractFile.value;
  if (!current) return false;
  try {
    const updated = await ContractsService.update(current.contract.uid, update);
    contractFile.value = {
      ...current,
      contract: { ...updated, matters: current.contract.matters },
    };
    message.success(success);
    return true;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update the contract');
    return false;
  }
}

const editingType = ref(false);

function startTypeEdit(): void {
  if (contractWriteBlock.value) return;
  void contractTypeStore.ensureLoaded();
  editingType.value = true;
}

/** A new type asks new questions, so the fields are read again. */
async function handleTypeChange(contractTypeUid: string): Promise<void> {
  editingType.value = false;
  if (contractTypeUid === contract.value?.contract_type?.uid) return;
  const saved = await updateContract(
    { contract_type_uid: contractTypeUid },
    'Contract type updated'
  );
  if (!saved) return;
  await fetchFields();
  void loadVersionEntries(true);
}

const editingTags = ref(false);
/**
 * The tags as last edited, until saved. The input reads them, so a tag added
 * while the previous save is in flight builds on it, not on the saved list.
 */
const pendingTags = ref<string[] | null>(null);
let tagsSave = Promise.resolve();

/** Saves one at a time, each from the last one's answer; a newer list skips the older. */
function handleTagsChange(tags: string[]): void {
  pendingTags.value = tags;
  tagsSave = tagsSave.then(async () => {
    if (pendingTags.value !== tags) return;
    await updateContract({ tags }, 'Tags updated');
    if (pendingTags.value === tags) pendingTags.value = null;
  });
}

const showMatterModal = ref(false);

async function handleMatterAssigned(matterUid: string): Promise<void> {
  if (!currentContractUid.value) return;
  try {
    await ContractsService.addMatterToContract(matterUid, currentContractUid.value);
    message.success('Matter assigned successfully');
    await refreshFileQuietly();
  } catch (error) {
    logger.error('Error assigning matter:', error);
    message.error(
      error instanceof Error ? error.message : 'Failed to assign matter. Please try again.'
    );
  }
}

function matterRoute(matterUid: string): { name: string; params: Record<string, string> } {
  return { name: 'contract-management-detail', params: { entityType: 'matter', uid: matterUid } };
}

// ==================== Signature ====================
const showSigningDrawer = ref(false);
/** The drawer opens in edit mode from the Status cell's pencil. */
const signingDrawerEditing = ref(false);

function openSigningDrawer(editing: boolean): void {
  signingDrawerEditing.value = editing;
  showSigningDrawer.value = true;
}

/** The file's signing requests with their status changes, for the drawer; null while loading. */
const signingHistory = ref<
  { request: SigningRequestWithRelationsInDb; entries: SigningRequestStatusEntry[] }[] | null
>(null);

/** Every request of the file — not just the read version's — each with its steps. */
async function loadSigningHistory(): Promise<void> {
  if (!authStore.userPermissions.can_use_signing) {
    signingHistory.value = [];
    return;
  }
  signingHistory.value = null;
  try {
    const requests = await ContractFileDetailService.getSigningRequests(contractFileUid.value);
    // One request's steps failing keeps the others: it shows without its steps.
    const failed: string[] = [];
    signingHistory.value = await Promise.all(
      requests.map(async request => {
        try {
          return {
            request,
            entries: (await SignatureRequestService.getStatusHistory(request.uid)) ?? [],
          };
        } catch (error) {
          logger.error('Error fetching signing request steps:', error);
          failed.push(request.uid);
          return { request, entries: [] };
        }
      })
    );
    if (failed.length) message.error('Failed to load some signing request steps');
  } catch (error) {
    logger.error('Error fetching signing history:', error);
    message.error(error instanceof Error ? error.message : 'Failed to load the signing history');
    signingHistory.value = [];
  }
}

watch(showSigningDrawer, open => {
  if (open) void loadSigningHistory();
});

const signatureCount = computed(() => selectedRevision.value?.signature_infos?.length ?? 0);

// ==================== Linked records: parties, clauses, obligations ====================
const linkType = ref<EntityType | null>(null);
const showLinkModal = ref(false);
/** The record whose unlink is in flight. */
const unlinkingUid = ref<string | null>(null);

function openLink(type: EntityType): void {
  linkType.value = type;
  showLinkModal.value = true;
}

async function handleLinked(entityUid: string, pageNumber?: number): Promise<void> {
  const pageNum = pageNumber !== undefined && pageNumber >= 0 ? pageNumber : null;
  try {
    switch (linkType.value) {
      case 'obligation':
        await ContractFilesService.addObligation(entityUid, contractFileUid.value, pageNum);
        message.success('Obligation assigned successfully');
        break;
      case 'clause':
        await ContractFilesService.addClause(entityUid, contractFileUid.value, pageNum);
        message.success('Clause assigned successfully');
        break;
      case 'contractual-party':
        await addContractualPartyToContractFile({
          query: { contractual_party_uid: entityUid, contract_file_uid: contractFileUid.value },
          throwOnError: true,
        });
        message.success('Contractual party assigned successfully');
        break;
      default:
        return;
    }
    await fetchCoreStaticModels();
  } catch (error) {
    logger.error('Error assigning entity:', error);
    message.error(
      error instanceof Error ? error.message : 'Failed to assign entity. Please try again.'
    );
  }
}

async function unlink(
  type: 'clause' | 'obligation' | 'contractual-party',
  uid: string,
  title: string
): Promise<void> {
  unlinkingUid.value = uid;
  try {
    if (type === 'clause') {
      await ContractFilesService.removeClause(uid, contractFileUid.value);
    } else if (type === 'obligation') {
      await ContractFilesService.removeObligation(uid, contractFileUid.value);
    } else {
      await removeContractualPartyFromContractFile({
        query: { contractual_party_uid: uid, contract_file_uid: contractFileUid.value },
        throwOnError: true,
      });
    }
    message.success(`${title} unlinked successfully`);
    await fetchCoreStaticModels();
  } catch (error) {
    logger.error('Error unlinking entity:', error);
    message.error(
      error instanceof Error ? error.message : 'Failed to unlink entity. Please try again.'
    );
  } finally {
    unlinkingUid.value = null;
  }
}

const clauseRows = computed<GaiaEntityListRow[]>(() =>
  clauses.value.map(clause => ({
    uid: clause.uid,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    title: clause.title || 'Untitled Clause',
    category: clause.category,
    ref: clause.clause_number ? `#${clause.clause_number}` : null,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    body: clause.description || clause.text,
    to: { name: 'contract-management-detail', params: { entityType: 'clause', uid: clause.uid } },
  }))
);

const obligationRows = computed<GaiaEntityListRow[]>(() =>
  obligations.value.map(obligation => ({
    uid: obligation.uid,
    title: obligation.name || 'Unnamed Obligation',
    category: obligation.type,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    body: obligation.description || obligation.trigger_event,
    to: {
      name: 'contract-management-detail',
      params: { entityType: 'obligation', uid: obligation.uid },
    },
  }))
);

// ==================== Term dates ====================
// Extracted, or added by hand from the Key dates row; a type may hold several.
const showDateForm = ref(false);
const editingDate = ref<ContractManagementDateInDb | null>(null);
/** The type a new date is created as — fixed by the row it was added from. */
const createDateType = ref('');
/** New dates go on the version being read. */
const dateFormConfig = computed(() =>
  fileDateConfig(contractFileUid.value, filterRevisionUid.value)
);

function openDateForm(date: ContractManagementDateInDb): void {
  editingDate.value = date;
  showDateForm.value = true;
}

function openDateCreate(type: string): void {
  editingDate.value = null;
  createDateType.value = type;
  showDateForm.value = true;
}

async function handleDeleteDate(date: ContractManagementDateInDb): Promise<void> {
  try {
    await deleteContractManagementDate({ path: { uid: date.uid }, throwOnError: true });
    message.success('Date deleted');
    await fetchCoreStaticModels();
  } catch (error) {
    logger.error('Error deleting date:', error);
    message.error(error instanceof Error ? error.message : 'Failed to delete date');
  }
}

// ==================== Tabs ====================
type FileTab = 'fields' | 'custom' | 'clauses' | 'obligations';
const activeTab = ref<FileTab>('fields');

const tabItems = computed<GaiaTabBarItem[]>(() => [
  // The term dates sit in the Fields table, pinned above the prompts.
  { key: 'fields', label: 'Type Extractions', count: fieldEntries.value.length },
  { key: 'custom', label: 'Custom Extractions', count: customFieldEntries.value.length },
  { key: 'clauses', label: 'Clauses', count: clauses.value.length },
  { key: 'obligations', label: 'Obligations', count: obligations.value.length },
]);

// ==================== Sharing ====================
const userShares = ref<ContractUserShareInDb[]>([]);
const roleShares = ref<ContractRoleShareInDb[]>([]);
const showShareModal = ref(false);

/** Who the contract is shared with — the Share button's faces and its hover list. */
async function loadShares(): Promise<void> {
  const uid = currentContractUid.value;
  if (!uid) return;
  try {
    const [users, roles] = await Promise.all([
      ContractShareService.getUserSharesByContract(uid),
      ContractShareService.getRoleSharesByContract(uid),
    ]);
    userShares.value = users;
    roleShares.value = roles;
  } catch (error) {
    // Non-critical: the button still opens the share dialog.
    logger.warn('Failed to load contract shares:', error);
  }
}

const shareRows = computed(() => [
  ...userShares.value.map(share => ({
    key: `user:${share.user.uid}`,

    name: share.user.full_name || share.user.email,
    meta: share.user.email,
    permissions: formatAccessPermissions(share.permissions),
  })),
  ...roleShares.value.map(share => ({
    key: `role:${share.role.uid}`,
    name: share.role.name,
    meta: 'Role',
    permissions: formatAccessPermissions(share.permissions),
  })),
]);

const shareAvatars = computed(() =>
  shareRows.value.map(row => ({ key: row.key, initials: initialsOf(row.name), name: row.name }))
);

// ==================== Extraction Edit Handlers ====================

// Get highlight text from extraction
// Uses highlighted_text if available, otherwise falls back to value as string
function getHighlightTextFromExtraction(
  extraction: ContractManagementExtractionInDb | null
): string[] {
  if (!extraction) return [];

  const highlightTexts: string[] = [];

  // Prioritize highlighted_text (from AI or human overwrite)
  const highlightedText =
    extraction.human_overwrite?.highlighted_text === undefined
      ? extraction.highlighted_text
      : extraction.human_overwrite.highlighted_text;

  if (highlightedText && Array.isArray(highlightedText)) {
    // Filter out empty strings and ensure reasonable length
    const validTexts = highlightedText
      .filter(text => text && typeof text === 'string' && text.trim().length >= 3)
      .map(text => text.trim())
      .slice(0, 10); // Limit to 10 texts to avoid performance issues

    highlightTexts.push(...validTexts);
  }

  // Fallback: use value as string if no highlighted_text available
  if (highlightTexts.length === 0) {
    const value =
      extraction.human_overwrite?.value === undefined
        ? extraction.value
        : extraction.human_overwrite.value;

    if (value !== null) {
      // Convert value to string, handling different types
      let valueStr = '';
      if (typeof value === 'string') {
        valueStr = value;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        valueStr = String(value);
      } else if (Array.isArray(value)) {
        valueStr = value.map(v => safeStringify(v)).join(' ');
      } else if (typeof value === 'object') {
        valueStr = JSON.stringify(value);
      }

      // Only add if it's a reasonable length
      const cleanValue = valueStr.trim();
      if (cleanValue.length >= 3 && cleanValue.length <= 200) {
        highlightTexts.push(cleanValue);
      }
    }
  }

  return highlightTexts;
}

// Handle page click from extractions
function handlePageClick(page: number, extraction: ContractManagementExtractionInDb): void {
  if (!vaultFileUid.value) {
    message.warning('Vault file not available for preview');
    return;
  }

  selectedPage.value = page;
  selectedExtraction.value = extraction;
  // A hidden preview reopens; handleDocumentLoad then jumps to the page.
  if (!showPdf.value) {
    showPdf.value = true;
    return;
  }
  showInPdf(page);
}

// Show page in PDF with highlighting
function showInPdf(page: number): void {
  if (pdfPreviewRef.value) {
    selectedPage.value = page;
    pdfPreviewRef.value.goToPage(page);

    // Only highlight if this page matches the extraction's page
    const shouldHighlight = isPageMatchingExtraction(page, selectedExtraction.value);

    if (shouldHighlight) {
      // Apply highlights for the selected extraction
      const highlightText = getHighlightTextFromExtraction(selectedExtraction.value);
      const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

      if (validHighlightText.length > 0) {
        currentHighlights.value = validHighlightText;

        // Set highlight text after a delay to ensure page is loaded
        setTimeout(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText(validHighlightText);
          }
        }, 500);
      } else {
        // Clear highlights if no valid text
        currentHighlights.value = [];
        void nextTick(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText([]);
          }
        });
      }
    } else {
      // Clear highlights if page doesn't match extraction's page
      currentHighlights.value = [];
      void nextTick(() => {
        if (pdfPreviewRef.value) {
          pdfPreviewRef.value.setHighlightText([]);
        }
      });
    }
  }
}

// Handle document load
function handleDocumentLoad(): void {
  if (selectedPage.value && pdfPreviewRef.value) {
    void nextTick(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
      const page = selectedPage.value || 1;
      pdfPreviewRef.value?.goToPage(page);

      // Apply highlights after page navigation, only if page matches extraction's page
      setTimeout(() => {
        if (pdfPreviewRef.value && selectedPage.value) {
          const shouldHighlight = isPageMatchingExtraction(
            selectedPage.value,
            selectedExtraction.value
          );

          if (shouldHighlight) {
            const highlightText = getHighlightTextFromExtraction(selectedExtraction.value);
            const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

            if (validHighlightText.length > 0) {
              pdfPreviewRef.value.setHighlightText(validHighlightText);
              currentHighlights.value = validHighlightText;
            } else {
              pdfPreviewRef.value.setHighlightText([]);
              currentHighlights.value = [];
            }
          } else {
            pdfPreviewRef.value.setHighlightText([]);
            currentHighlights.value = [];
          }
        }
      }, 500); // Delay to ensure page and text layer are fully rendered
    });
  }
}

// Handle highlights cleared
function handleHighlightsCleared(): void {
  currentHighlights.value = [];
}

// Check if a page matches the extraction's page
function isPageMatchingExtraction(
  page: number,
  extraction: ContractManagementExtractionInDb | null
): boolean {
  if (!extraction) return false;

  // Get the extraction's page (from human overwrite or original)
  const extractionPage =
    extraction.human_overwrite?.page === undefined
      ? extraction.page
      : extraction.human_overwrite.page;

  // Check if the current page matches the extraction's page
  return extractionPage !== null && extractionPage === page;
}

// Handle PDF page change
function handlePdfPageChange(page: number): void {
  selectedPage.value = page;

  // Update highlights when page changes (only if we have a selected extraction)
  if (pdfPreviewRef.value && selectedExtraction.value) {
    // Only highlight if this page matches the extraction's page
    const shouldHighlight = isPageMatchingExtraction(page, selectedExtraction.value);

    if (shouldHighlight) {
      const highlightText = getHighlightTextFromExtraction(selectedExtraction.value);
      const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

      if (validHighlightText.length > 0) {
        // Apply highlights for this page with a delay to ensure text layer is ready
        setTimeout(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText(validHighlightText);
            currentHighlights.value = validHighlightText;
          }
        }, 300);
      } else {
        // Clear highlights if no valid text
        currentHighlights.value = [];
        void nextTick(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText([]);
          }
        });
      }
    } else {
      // Clear highlights if page doesn't match extraction's page
      currentHighlights.value = [];
      void nextTick(() => {
        if (pdfPreviewRef.value) {
          pdfPreviewRef.value.setHighlightText([]);
        }
      });
    }
  }
}

function handleEditExtraction(extraction: ContractManagementExtractionInDb): void {
  // Create a minimal column object since we don't have full column data in this view
  // The modal will work with just the extraction data
  const columnData: ContractManagementViewColumnInDb | null = {
    uid: extraction.uid, // Use extraction uid as column uid
    name: extraction.prompt, // Use prompt as column name
    extraction_prompt: {
      prompt: extraction.prompt,
      response_type: extraction.response_type,
      example_data: extraction.example_data,
    },
  } as ContractManagementViewColumnInDb;

  editingExtraction.value = {
    columnUid: extraction.uid,
    extraction,
    column: columnData,
    contractUid: contractFile.value?.contract.uid,
    fileUid: contractFile.value?.uid,
  };

  showExtractionEditModal.value = true;
}

/**
 * Handle extraction update - refresh the fields
 */
async function handleExtractionUpdate(): Promise<void> {
  await fetchFields(true);
  showExtractionEditModal.value = false;
}

/**
 * Handle static models refresh (called by child component after mutations)
 */
async function handleStaticModelsRefresh(): Promise<void> {
  await fetchCoreStaticModels();
}

// ==================== Live updates ====================

/**
 * Re-read the file and its versions without the header shimmer, keeping the
 * picked version — a version's effective state follows its dates, so the
 * Status cell reads off the fresh list.
 */
async function refreshFileQuietly(): Promise<void> {
  const vaultUid = vaultFileUid.value;
  if (!vaultUid) return;
  try {
    const [file, { results }] = await Promise.all([
      ContractFileDetailService.getContractFile(contractFileUid.value),
      vaultStore.listRevisions(vaultUid, 50, 0),
    ]);
    contractFile.value = file;
    revisions.value = results;
  } catch (error) {
    logger.error('Error refreshing contract file:', error);
  }
}

/** Finished extraction states — anything else is still on its way. */
const SETTLED = new Set<unknown>([TaskStatus.DONE, TaskStatus.FAILED, TaskStatus.PARTIAL]);

// A fresh upload settles its tasks one after another; the re-read is
// debounced into one rather than a full round of requests per task.
const refreshAfterFileExtractions = useDebounceFn((): void => {
  void Promise.all([fetchCoreStaticModels(), fetchFields(true)]);
  void loadVersionEntries();
}, 500);

// The backend sends `vault_file_updated` on any change to the file's state —
// a date added, edited or deleted, from here or another tab — and a model
// extraction event as each built-in extraction settles. Both name the vault
// file, not a version, so the open version is simply re-read.
useVaultWebSocket({
  fileUids: () => (vaultFileUid.value ? [vaultFileUid.value] : []),
  onFileUpdated: () => {
    void Promise.all([refreshFileQuietly(), fetchCoreStaticModels()]);
  },
  onFileModelExtractionUpdated: (_uid, data) => {
    if (!SETTLED.has(data.status)) return;
    // Classification sets the file's type — and with it, which fields are
    // asked; every other task fills the static models or answers the fields.
    if (data.task_type === 'contract_management_classify_contract_file') {
      void refreshFileQuietly().then(refreshAfterFileExtractions);
    } else {
      void refreshAfterFileExtractions();
    }
  },
});

// A rerun puts the read version's extractions back to running; each one's
// settling arrives on its own uid. Many settle together, so the re-read is
// debounced into one.
const refreshSettledFields = useDebounceFn((): void => {
  void fetchFields(true);
  void loadVersionEntries();
}, 500);

useContractManagementExtractionWebSocket({
  extractionUids: () =>
    [...fieldEntries.value, ...customFieldEntries.value].flatMap(entry =>
      entry.extraction && !SETTLED.has(entry.extraction.processing_state)
        ? [entry.extraction.uid]
        : []
    ),
  onExtractionUpdated: (_uid, data) => {
    if (SETTLED.has(data.processing_state)) void refreshSettledFields();
  },
});

// ==================== Lifecycle ====================

onMounted(async () => {
  await fetchContractFile();
  // Everything below hangs off the file; a refused file means refused children.
  if (unavailable.value) return;
  // Load vault file revisions for the Versions table (non-critical)
  await loadRevisions();
  void loadShares();
  void loadVersionEntries();
  await Promise.all([fetchCoreStaticModels(), fetchFields()]);
});

// The page body — the tab card and the versions — drawn once, placed either
// full width or in the split's left pane beside the document.
const [DefineBody, ReuseBody] = createReusableTemplate();
</script>

<template>
  <!-- The signing history opens beside the record rather than over it: the layout's drawer
       squeezes the page, so the record stays in view. -->
  <Layout
    :content-max-width="gaiaContentWidth.record"
    :drawer-open="showSigningDrawer"
    :drawer-width="420"
  >
    <template #breadcrumb>
      <n-breadcrumb-item v-if="unavailable">{{ navLabels.fileVault }}</n-breadcrumb-item>
      <template v-else>
        <!-- The trail ends with the file's contract. -->
        <gaia-vault-crumbs kind="contract_file" :uid="contractFileUid" />
        <n-breadcrumb-item>{{ vaultFileName }}</n-breadcrumb-item>
      </template>
    </template>

    <DefineBody>
      <n-flex vertical :size="24">
        <gaia-tab-card
          data-tour="cm.file-extractions"
          :items="tabItems"
          :active="activeTab"
          @select="(key: string) => (activeTab = key as FileTab)"
        >
          <template
            v-if="activeTab === 'clauses' || activeTab === 'obligations' || vaultFileUid"
            #actions
          >
            <gaia-add-button
              v-if="activeTab === 'clauses' || activeTab === 'obligations'"
              :subject="activeTab === 'clauses' ? 'clause' : 'obligation'"
              :disabled="writeBlock !== null"
              :title="writeBlock ?? ''"
              @click="openLink(activeTab === 'clauses' ? 'clause' : 'obligation')"
            />
            <!-- The tab bar is inset 12px, the fields table 24px: the margin
                 lines the toggle up with the table's "Show per version" and
                 page chips. -->
            <gaia-tooltip v-if="vaultFileUid">
              <template #trigger>
                <gaia-icon-button
                  :dimension="28"
                  :style="{ marginRight: '12px' }"
                  @click="showPdf = !showPdf"
                >
                  <n-icon :component="showPdf ? IconEyeOff : IconEye" :size="15" />
                </gaia-icon-button>
              </template>
              {{ showPdf ? 'Hide PDF Preview' : 'Show PDF Preview' }}
            </gaia-tooltip>
          </template>

          <gaia-contract-type-fields-table
            v-if="activeTab === 'fields'"
            v-model:comparing="comparingVersions"
            :entries="fieldEntries"
            :loading="loadingFields"
            :file-name="vaultFileName"
            :can-compare="revisions.length > 1"
            :versions="comparedVersions"
            :can-rerun="canRerunExtractions"
            @edit="handleEditExtraction"
            @open-page="handlePageClick"
            @rerun="handleRerunExtraction"
          >
            <!-- No type, no questions: the type decides which fields are asked. -->
            <template v-if="!loadingFile && !contract?.contract_type" #empty>
              <gaia-empty-state title="No contract type set">
                Fields are extracted from the contract type's prompts. Set a type to extract them.
                <template #actions>
                  <gaia-button
                    size="small"
                    :disabled="contractWriteBlock !== null"
                    :title="contractWriteBlock ?? undefined"
                    @click="startTypeEdit"
                  >
                    Set type
                  </gaia-button>
                </template>
              </gaia-empty-state>
            </template>
          </gaia-contract-type-fields-table>

          <!-- Standalone prompts: questions asked outside the contract type. -->
          <gaia-contract-type-fields-table
            v-else-if="activeTab === 'custom'"
            v-model:comparing="comparingVersions"
            :entries="customFieldEntries"
            :loading="loadingFields"
            :file-name="vaultFileName"
            :can-compare="revisions.length > 1"
            :versions="comparedCustomVersions"
            :can-rerun="canRerunExtractions"
            @edit="handleEditExtraction"
            @open-page="handlePageClick"
            @rerun="handleRerunExtraction"
          >
            <template #empty>
              <gaia-empty-state title="No custom extractions">
                Custom extractions are answers to questions asked outside the contract type, such as
                a question column in a view.
              </gaia-empty-state>
            </template>
          </gaia-contract-type-fields-table>

          <gaia-contract-file-entity-list
            v-else-if="activeTab === 'clauses'"
            noun="clause"
            :rows="clauseRows"
            :loading="loadingStaticModels"
            :busy-uid="unlinkingUid"
            :write-block="writeBlock"
            @add="openLink('clause')"
            @remove="(row: GaiaEntityListRow) => unlink('clause', row.uid, row.title)"
          />

          <gaia-contract-file-entity-list
            v-else-if="activeTab === 'obligations'"
            noun="obligation"
            :rows="obligationRows"
            :loading="loadingStaticModels"
            :busy-uid="unlinkingUid"
            :write-block="writeBlock"
            @add="openLink('obligation')"
            @remove="(row: GaiaEntityListRow) => unlink('obligation', row.uid, row.title)"
          />
        </gaia-tab-card>

        <gaia-contract-file-versions-table
          v-if="revisions.length"
          :revisions="revisions"
          :current-uid="currentRevisionUid"
          :selected-uid="selectedRevisionUid"
          :field-counts="fieldCounts"
          :has-state="hasState"
          :write-block="writeBlock"
          @select="(uid: string) => (selectedRevisionUid = uid)"
          @add="showRevisionUploadModal = true"
        />
      </n-flex>
    </DefineBody>

    <!-- The file is gone, or not visible from the active organisation. -->
    <gaia-not-found v-if="unavailable" />
    <n-flex v-else-if="loadingFile && !contractFile" justify="center" :style="{ padding: '64px' }">
      <gaia-spinner />
    </n-flex>
    <n-flex v-else vertical :size="24">
      <!-- Header: what the file is, its summary, and the actions. -->
      <gaia-page-header :title="vaultFileName" size="screen">
        <template #eyebrow>
          <gaia-text variant="label" size="nano" tone="faint">
            {{ fileType || 'Contract file' }}
          </gaia-text>
          <gaia-count-pill
            v-if="revisions.length"
            :title="`${revisions.length} ${revisions.length === 1 ? 'version' : 'versions'}`"
          >
            {{ revisions.length }} {{ revisions.length === 1 ? 'version' : 'versions' }}
          </gaia-count-pill>
          <gaia-text size="micro" tone="faint">
            Created {{ createdAt }} · Updated {{ updatedAt }}
          </gaia-text>
        </template>
        <template #summary>
          <gaia-text
            v-if="contractFileDescription"
            size="body"
            tone="tertiary"
            :style="{ lineHeight: 1.55 }"
          >
            {{ contractFileDescription }}
          </gaia-text>
        </template>
        <template #actions>
          <n-flex align="center" :size="8" :wrap="false" data-tour="cm.file-actions">
            <!-- Who the contract is shared with: the faces, the list on hover,
                 and the share dialog on click. -->
            <gaia-popover trigger="hover" placement="bottom-end" :delay="150">
              <template #trigger>
                <gaia-button
                  size="medium"
                  :disabled="shareBlock !== null"
                  :title="shareBlock ?? undefined"
                  @click="showShareModal = true"
                >
                  <n-flex align="center" :size="8" :wrap="false">
                    <gaia-avatar-stack v-if="shareAvatars.length" :items="shareAvatars" />
                    Share
                  </n-flex>
                </gaia-button>
              </template>
              <gaia-menu-surface width="320px">
                <gaia-text
                  variant="label"
                  size="nano"
                  tone="subtle"
                  :style="{ padding: '6px 8px' }"
                >
                  Shared with
                </gaia-text>
                <gaia-picker-option
                  v-for="row in shareRows"
                  :key="row.key"
                  :label="row.name"
                  :meta="row.meta"
                  :initials="initialsOf(row.name)"
                >
                  <template #trailing>
                    <gaia-text size="meta" tone="tertiary">{{ row.permissions }}</gaia-text>
                  </template>
                </gaia-picker-option>
                <gaia-text
                  v-if="shareRows.length === 0"
                  size="meta"
                  tone="subtle"
                  :style="{ padding: '6px 8px' }"
                >
                  Not shared with anyone yet.
                </gaia-text>
              </gaia-menu-surface>
            </gaia-popover>

            <!-- Signing is the one thing this page exists to start, so it is the
                 only action with a primary — once signed, taking the signed copy
                 is. Preview and the rest sit in the ⋯ below. -->
            <gaia-button
              v-if="isSigned"
              type="primary"
              size="medium"
              :loading="downloading"
              @click="handleDownload"
            >
              Download signed
            </gaia-button>
            <gaia-button
              v-else-if="authStore.userPermissions.can_use_signing"
              type="primary"
              size="medium"
              :disabled="signBlock !== null"
              :title="signBlock ?? undefined"
              @click="handleSignThis"
            >
              <template #icon>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M2.5 13.5h11" />
                  <path
                    d="M3.8 10.8c1.4 0 1.8-6.3 3.2-6.3 1 0 .6 4.6.1 5.6-.3.6.4.9 1.1.3.8-.7 1.4-1.6 2.2-1.6.9 0 .6 1.6 1.8 1.6"
                  />
                </svg>
              </template>
              Sign this
            </gaia-button>

            <gaia-dropdown
              :options="headerMenuOptions"
              placement="bottom-end"
              @select="(key: string) => onHeaderMenu(key)"
            >
              <gaia-icon-button :dimension="36" title="More actions">
                <n-icon :component="IconMore" :size="18" />
              </gaia-icon-button>
            </gaia-dropdown>
          </n-flex>
        </template>
      </gaia-page-header>

      <!-- Reading an older revision is a mode, not a filter: say so, and give
           the way back rather than leaving the reader to notice. -->
      <gaia-banner v-if="isPreviewing" plain type="info">
        Previewing entities from {{ previewRevisionLabel }} — the current version is v{{
          vaultFileVersionNumber
        }}.
        <template #action>
          <gaia-button text size="tiny" @click="selectedRevisionUid = currentRevisionUid">
            Back to current
          </gaia-button>
        </template>
      </gaia-banner>

      <!-- Meta strip: the version's signature state, then the contract's own
           matter and type. When the version was made heads the page. -->
      <gaia-meta-strip columns="repeat(3, minmax(0, 1fr))">
        <gaia-meta-item label="Status" stacked clickable @click="openSigningDrawer(false)">
          <template #actions>
            <gaia-edit-button
              v-if="hasState"
              size="xs"
              subject="signature status"
              :disabled="writeBlock !== null"
              @click.stop="openSigningDrawer(true)"
            />
            <gaia-icon-button :dimension="20" quaternary title="Signing history">
              <n-icon :component="IconInfo" :size="13" />
            </gaia-icon-button>
          </template>
          <gaia-contract-state-cell
            v-if="hasState && selectedRevision"
            :state="selectedRevision.effective_state"
            :reasons="selectedRevision.needs_attention_reasons"
            :failure-reason="selectedRevision.signature_failure_reason ?? null"
            :signature-infos="selectedRevision.signature_infos ?? null"
          />
          <template v-else>{{ hasState ? '—' : 'Not tracked for appendices' }}</template>
          <template v-if="signatureCount" #sub>
            {{ signatureCount }} {{ signatureCount === 1 ? 'signature' : 'signatures' }} found
          </template>
        </gaia-meta-item>

        <gaia-meta-item
          label="Matter"
          stacked
          :clickable="contractWriteBlock === null"
          @click="showMatterModal = true"
        >
          <template #actions>
            <gaia-edit-button v-if="contractWriteBlock === null" size="xs" subject="matter" />
          </template>
          <router-link
            v-if="contract?.matters.length"
            :to="matterRoute(contract.matters[0].uid)"
            style="color: inherit"
            @click.stop
          >
            {{ contract.matters[0].name }}
          </router-link>
          <gaia-text v-else size="dense" tone="subtle">No matter</gaia-text>
          <template v-if="(contract?.matters.length ?? 0) > 1" #sub>
            +{{ (contract?.matters.length ?? 0) - 1 }} more on the contract
          </template>
        </gaia-meta-item>

        <gaia-meta-item
          label="Type"
          stacked
          :clickable="contractWriteBlock === null && !editingType"
          @click="startTypeEdit"
        >
          <template v-if="!editingType" #actions>
            <gaia-edit-button v-if="contractWriteBlock === null" size="xs" subject="type" />
          </template>
          <gaia-select
            v-if="editingType"
            size="small"
            :value="contract?.contract_type?.uid ?? null"
            :options="contractTypeStore.uidOptions"
            :loading="contractTypeStore.loading"
            placeholder="Choose a type"
            filterable
            default-show
            :style="{ width: '100%', minWidth: '160px' }"
            @update:value="handleTypeChange"
            @blur="editingType = false"
            @click.stop
          />
          <template v-else-if="contract?.contract_type">{{ contract.contract_type.name }}</template>
          <gaia-text v-else size="dense" tone="subtle">Not set</gaia-text>
        </gaia-meta-item>
      </gaia-meta-strip>

      <n-flex vertical :size="12" data-tour="cm.file-entities">
        <gaia-field-row label="Key dates" :label-width="130" :divider="false" dense>
          <gaia-contract-file-date-chips
            :dates="dates"
            :loading="loadingStaticModels && dates.length === 0"
            :write-block="writeBlock"
            @edit="openDateForm"
            @add="openDateCreate"
            @delete="handleDeleteDate"
          />
        </gaia-field-row>
        <gaia-field-row label="Contractual party" :label-width="130" :divider="false" dense>
          <gaia-contract-file-party-chips
            :parties="contractualParties"
            :loading="loadingStaticModels && contractualParties.length === 0"
            :write-block="writeBlock"
            @add="openLink('contractual-party')"
            @remove="
              (party: ContractualParty) => unlink('contractual-party', party.uid, party.name)
            "
          />
        </gaia-field-row>
        <gaia-field-row label="Tags" :label-width="130" :divider="false" dense>
          <gaia-tag-input
            v-if="editingTags"
            :value="pendingTags ?? contract?.tags ?? []"
            size="small"
            :style="{ minWidth: '280px' }"
            @update:value="handleTagsChange"
            @blur="editingTags = false"
          />
          <n-flex v-else align="center" :size="6">
            <gaia-tag v-for="tag in contract?.tags ?? []" :key="tag" size="small">
              {{ tag }}
            </gaia-tag>
            <gaia-add-button
              size="xs"
              variant="dashed"
              subject="tag"
              :disabled="contractWriteBlock !== null"
              :title="contractWriteBlock ?? ''"
              @click="editingTags = true"
            />
          </n-flex>
        </gaia-field-row>
      </n-flex>

      <ReuseBody v-if="!vaultFileUid || !showPdf" />

      <!-- The body on the left, the document on the right, in the golden ratio. -->
      <gaia-split
        v-else
        :max="0.75"
        :min="0.35"
        :default-size="0.618"
        :style="{ height: 'calc(100vh - 160px)' }"
        @update:size="handleSplitResize"
      >
        <template #1>
          <div :style="{ height: '100%', overflowY: 'auto', paddingRight: '12px' }">
            <ReuseBody />
          </div>
        </template>
        <template #2>
          <!-- Plain flex boxes with a definite height all the way down: the
               preview's root is `height: 100%`, and inside a wrapping n-flex
               that resolves to nothing and the document collapses to 0px. -->
          <div
            :style="{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              paddingLeft: '12px',
              overflow: 'hidden',
            }"
          >
            <div
              :style="{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
              }"
            >
              <gaia-vault-file-pdf-preview
                ref="pdfPreviewRef"
                :key="pdfKey"
                :vault-file-uid="vaultFileUid"
                :revision-uid="selectedRevisionUid || undefined"
                :revision-preview-status="selectedRevision?.preview_status ?? undefined"
                @loaded="handleDocumentLoad"
                @highlights-cleared="handleHighlightsCleared"
                @page-change="handlePdfPageChange"
              />
            </div>
          </div>
        </template>
      </gaia-split>
    </n-flex>

    <SelectEntityModal
      v-if="linkType"
      v-model:show="showLinkModal"
      :entity-type="linkType"
      :contract-file-uid="contractFileUid"
      @assigned="handleLinked"
    />

    <SelectEntityModal
      v-if="currentContractUid"
      v-model:show="showMatterModal"
      entity-type="matter"
      :contract-uid="currentContractUid"
      @assigned="handleMatterAssigned"
    />

    <mass-share-contracts-modal
      v-model:show="showShareModal"
      :contract-uids="currentContractUid ? [currentContractUid] : []"
      :contract-names="contract?.name ? [contract.name] : []"
      @shared="loadShares"
    />

    <entity-form-dialog
      v-model:show="showDateForm"
      :config="dateFormConfig"
      :row="editingDate ? toDateRow(editingDate) : null"
      :row-name="editingDate ? staticEntityName(editingDate) : ''"
      :defaults="{ type: createDateType }"
      :locked-keys="['type']"
      @saved="handleStaticModelsRefresh"
    />

    <MoveContractFileModal
      v-model:show="showMoveModal"
      :contract-file-uid="contractFileUid"
      @moved="handleMoved"
    />

    <gaia-modal
      :show="showEditModal"
      width="460px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && handleCancelEdit()"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">Edit contract file</gaia-text>

      <n-flex vertical :size="14" :style="{ marginTop: '16px' }">
        <gaia-form-field label="File type" required>
          <gaia-select
            :value="editFormData.type"
            :options="fileTypeOptions"
            placeholder="Not classified yet"
            style="width: 100%"
            @update:value="
              (value: ContractManagementContractFileType) => (editFormData.type = value)
            "
          />
        </gaia-form-field>

        <gaia-form-field label="Description" hint="Optional">
          <gaia-textarea
            :value="editFormData.description"
            :autosize="{ minRows: 3 }"
            style="width: 100%"
            @update:value="(value: string) => (editFormData.description = value)"
          />
        </gaia-form-field>
      </n-flex>

      <n-flex justify="end" :size="8" :style="{ marginTop: '18px' }">
        <gaia-button size="small" @click="handleCancelEdit">Cancel</gaia-button>
        <gaia-button type="primary" size="small" :loading="saving" @click="handleSaveEdit">
          {{ saving ? 'Saving…' : 'Save changes' }}
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <!-- Deleting the file acts on the whole page, so it stays a dialog. -->
    <gaia-modal
      :show="confirmDelete"
      width="440px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && (confirmDelete = false)"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">
        Delete “{{ vaultFileName }}”?
      </gaia-text>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginTop: '10px', lineHeight: 1.55 }"
      >
        The document is deleted with all of its versions and extractions. If it is the contract's
        last file, the contract is deleted too.
      </gaia-text>
      <n-flex justify="end" :size="8" :style="{ marginTop: '20px' }">
        <gaia-button size="small" @click="confirmDelete = false">Cancel</gaia-button>
        <gaia-button type="error" size="small" :loading="deleting" @click="confirmDeleteNow">
          Delete file
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <!-- Rerunning replaces every answer of the read version, so it asks first. -->
    <gaia-modal
      :show="confirmRerunAll"
      width="440px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && (confirmRerunAll = false)"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">
        {{
          isPreviewing
            ? `Rerun all extractions of ${previewRevisionLabel}?`
            : 'Rerun all extractions?'
        }}
      </gaia-text>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginTop: '10px', lineHeight: 1.55 }"
      >
        Every question asked of this version is asked anew. Their answers are replaced, manual
        overwrites included. Costs one credit per question.
      </gaia-text>
      <n-flex justify="end" :size="8" :style="{ marginTop: '20px' }">
        <gaia-button size="small" @click="confirmRerunAll = false">Cancel</gaia-button>
        <gaia-button type="primary" size="small" @click="confirmRerunAllNow">
          Rerun all
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <ContractManagementExtractionDetailsModal
      v-if="editingExtraction"
      v-model:show="showExtractionEditModal"
      :cell-data="editingExtraction"
      :on-data-update="handleExtractionUpdate"
    />

    <VaultFileRevisionUploadModal
      v-if="vaultFileUid"
      v-model:show="showRevisionUploadModal"
      :file-uid="vaultFileUid"
      :file-name="vaultFileName"
      @uploaded="handleRevisionUploaded"
    />
    <template #drawer>
      <gaia-signing-history-panel
        v-model:show="showSigningDrawer"
        v-model:editing="signingDrawerEditing"
        :revisions="revisions"
        :read-revision="selectedRevision"
        :file-name="vaultFileName"
        :signing-history="signingHistory"
        :write-block="hasState ? writeBlock : 'Not tracked for appendices'"
        @set-state="setRevisionState"
      />
    </template>
  </Layout>
</template>
